import { useForm } from "react-hook-form";
import { FC, useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import SearchTable from "../../components/template/Search/SearchTable";
import { useFilter } from "../../hooks/filter/useFilter";
import { PAGE_SIZE_OPTIONS, ROLE_SUMMARY } from "../../utils/constants";
import { useAuth } from "../../provider/AuthProvider";
import { RolesItemSchema } from "./data/RolesItemSchema";
import SelectComponent from "../../components/molecule/Select/SelectComponent";
import TableRoles from "./TableRoles";
import { getRole } from "../../services/getRole";
import { getRoleLevels } from "../../services/getRoleLevels";
import { Option } from "../../types/General/Option";
import { TableConfig } from "../../types/Components/Table/TableConfig";
import { FilterConfig } from "../../types/Components/Table/FilterConfig";
import useFetchData from "../../hooks/data/useFetchData";
import useTableRendering from "../../hooks/table/useTableRendering";
import { PaginationConfig } from "../../types/Components/Table/PaginationConfig";
import { getFiltersArray } from "../../helpers/getFiltersArray";
import { PaginationValues } from "../../types/General/PaginationValues";

const Roles: FC = () => {
  const navigate = useNavigate();
  const [selectedFilterItem, setSelectedFilterItem] = useState<string>("");
  const [dataTable, setDataTable] = useState<any[]>([]);
  const [errorFindingData, setErrorFindingData] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [organizationLevelOptions, setOrganizationLevelOptions] = useState<
    Option[]
  >([]);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isSearching, setIsSearching] = useState<boolean>(true);
  const { auth } = useAuth();
  const user = auth.user;

  const { control, setValue } = useForm({
    resolver: zodResolver(RolesItemSchema),
  });

  const filterNames = useMemo(() => ["RoleLevelId", "Name"], []);
  const { filters, setFilter } = useFilter({ setValue, filterNames });

  const { isLoaded: levelLoaded } = useFetchData(
    getRoleLevels,
    setOrganizationLevelOptions,
  );

  useEffect(() => {
    const fetchData = (
      filterValues?: any,
      paginationValues?: PaginationValues,
    ) => {
      setErrorFindingData(false);
      setIsLoaded(false);
      setTimeout(() => {
        getRole(filterValues, paginationValues)
          .then((result) => {
            setDataTable(result.content);
            setTotalItems(result.totalItems);
            setIsLoaded(true);
            setIsSearching(false);
          })
          .catch((error) => {
            console.log("API Error:", error);
            setErrorFindingData(true);
            setIsSearching(false);
          });
      }, 1000);
    };
    isSearching &&
      fetchData(filters, {
        CurrentPage: currentPage,
        PageSize: pageSize,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearching]);

  const filtersArray = getFiltersArray(filterNames, filters, setFilter);

  const filterChildren = (
    <SelectComponent
      fieldNameObject="RoleLevelId"
      options={organizationLevelOptions}
      fieldName="Tipo de entidade"
      width="230px"
      control={control}
      onChange={(e) => {
        setFilter("RoleLevelId", e.target.value);
        e.target.selectedOptions[0].text !== "Seleccionar" &&
          setSelectedFilterItem(e.target.selectedOptions[0].text);
      }}
      isLoaded={levelLoaded}
    />
  );

  const handleAddNewItem = () => {
    navigate("/perfis/criar-perfis");
  };

  const tableConfig: TableConfig = {
    tableName: "Lista de Perfis",
    dataTable,
    setDataTable,
    renderTable: (dataTable) => (
      <TableRoles dataTable={dataTable} isLoaded={isLoaded} user={user} />
    ),
    isLoaded,
    errorFindingData,
    searchTitle: "Pesquisar pelo Nome do Perfil",
  };

  const filterConfig: FilterConfig = {
    filters: filtersArray,
    filterChildren,
    selectedFilterItem: selectedFilterItem,
  };

  const { currentPage, totalPages, handlePageChange, renderContent } =
    useTableRendering(tableConfig, pageSize, totalItems, setIsSearching);

  const paginationConfig: PaginationConfig = {
    pageSize,
    currentPage,
    totalPages,
    handlePageChange,
    renderContent,
    setPageSize,
    totalItems,
  };

  return (
    <SearchTable
      handleAddNewItem={
        user.permissions.includes("CreateRole") ? handleAddNewItem : null
      }
      summaryName={ROLE_SUMMARY}
      showModal
      searchTableName="Perfis"
      createButtonName="Criar perfil"
      tableConfig={tableConfig}
      filterConfig={filterConfig}
      paginationConfig={paginationConfig}
      setIsSearching={setIsSearching}
    ></SearchTable>
  );
};

export default Roles;
